'use client';
import {config} from '@config';
import {Toaster} from '@lib/components/ui/sonner';
import {ApplicationMetadataProvider} from '@stores/application-metadata';
import {IntegrationManifestsProvider} from '@stores/integration-manifests';
import type {IState} from 'flagsmith/types';
import type {Session} from 'next-auth';
import {SessionProvider} from 'next-auth/react';
import {usePathname} from 'next/navigation';
import {NuqsAdapter} from 'nuqs/adapters/next/app';
import type {ReactNode} from 'react';
import {SWRConfig} from 'swr';

import Loading from './(auth)/loading';
import {DatadogFlagsLogger} from './_components/DatadogFlagsLogger';
import {FlagsProvider} from './_components/FlagsProvider';
import {StylesProvider} from './_components/StylesProvider';
import useAuthGuard from './_hooks/useAuthGuard';
import {useApplicationStartLogger, useDatadog} from './_hooks/useDatadog';
import {BackgroundTasksProvider} from './_stores/background-tasks/provider';
import {useBackgroundTasksNotifications} from './_stores/background-tasks/useBackgroundTasksNotifications';

const {
  nextauth: {
    pages: {signIn},
  },
} = config;

const InternalProviders = ({
  children,
  featureFlagsState,
}: {
  children?: ReactNode;
  featureFlagsState: IState;
  session: Session | null;
  colorModeCookie?: string;
}) => {
  const pathname = usePathname();

  const isPovTenantEnabled = featureFlagsState.flags?.ui_pov_tenant?.enabled ?? false;

  useAuthGuard({signIn});
  useBackgroundTasksNotifications();
  useDatadog({isPovTenantEnabled});
  useApplicationStartLogger(pathname);

  return (
    <FlagsProvider flagsmithState={featureFlagsState}>
      <DatadogFlagsLogger>
        <>{children || <Loading />}</>
      </DatadogFlagsLogger>
    </FlagsProvider>
  );
};

export const Providers = (props: Parameters<typeof InternalProviders>[0]) => {
  return (
    <NuqsAdapter>
      <Toaster expand visibleToasts={5} />
      <StylesProvider
        colorModeCookie={props.colorModeCookie}
        newUIEnabled={Boolean(props.featureFlagsState.flags?.ui_new_ui?.enabled)}
      >
        <SessionProvider>
          <SWRConfig value={{revalidateOnFocus: false}}>
            <ApplicationMetadataProvider>
              <IntegrationManifestsProvider>
                <BackgroundTasksProvider>
                  <InternalProviders {...props} />
                </BackgroundTasksProvider>
              </IntegrationManifestsProvider>
            </ApplicationMetadataProvider>
          </SWRConfig>
        </SessionProvider>
      </StylesProvider>
    </NuqsAdapter>
  );
};
